import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { patientApi } from './patient.api'
import type { Patient, PatientFilters } from './patient.types'
import { useAppDispatch, showLoader, hideLoader, showSnackbar } from '@/store'
import { getResponseMessage } from '@/utils'

const patientKeys = {
  all: () => ['patients'] as const,
  list: (filters?: object) => ['patients', 'list', filters] as const,
  infinite: (filters?: object) => ['patients', 'infinite', filters] as const,
  detail: (uuid: string) => ['patients', 'detail', uuid] as const,
  profile: () => ['patients', 'profile'] as const,
}

// GET /api/master/patient — list with filters
export const usePatients = (filters?: PatientFilters, enabled = true) =>
  useQuery({
    queryKey: patientKeys.list(filters),
    queryFn: () => patientApi.getAll(filters),
    enabled,
    staleTime: 0,
  })

// GET /api/master/patient — paginated, fetches next page on demand
export const useInfinitePatients = (
  filters?: Omit<PatientFilters, 'page'>,
  enabled = true,
  pageSize = 10,
) =>
  useInfiniteQuery({
    queryKey: patientKeys.infinite({ ...filters, size: pageSize }),
    queryFn: ({ pageParam = 0 }) =>
      patientApi.getAll({ ...filters, page: pageParam as number, size: pageSize }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      (lastPage?.content?.length ?? 0) < pageSize ? undefined : allPages.length,
    enabled,
  })

// GET /api/master/patient/{patientId}
export const usePatient = (uuid: string) =>
  useQuery({
    queryKey: patientKeys.detail(uuid),
    queryFn: () => patientApi.getById(uuid),
    enabled: Boolean(uuid),
  })

// GET /api/master/patient/profile
export const usePatientProfile = () =>
  useQuery({
    queryKey: patientKeys.profile(),
    queryFn: () => patientApi.getProfile(),
  })

// GET /api/master/patient/{patientId}/signature
export const usePatientSignature = (patientId?: string, enabled = true) =>
  useQuery({
    queryKey: [...patientKeys.detail(patientId ?? ''), 'signature'] as const,
    queryFn: () => patientApi.getSignature(patientId as string),
    enabled: enabled && !!patientId,
    staleTime: 0,
  })

// POST /api/master/patient
export const useCreatePatient = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: (payload: Patient) => patientApi.create(payload),
    onMutate: () => {
      dispatch(showLoader())
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: patientKeys.all() })
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'success',
          message: getResponseMessage(response, 'Patient created successfully!'),
        }),
      )
    },
    onError: (error: unknown) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'error',
          message: getResponseMessage(error, 'Failed to create patient'),
        }),
      )
    },
  })
}

// PUT /api/master/patient (uuid in payload body)
export const useUpdatePatient = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: (payload: Partial<Patient>) => patientApi.update(payload),
    onMutate: () => {
      dispatch(showLoader())
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: patientKeys.all() })
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'success',
          message: getResponseMessage(response, 'Patient updated successfully!'),
        }),
      )
    },
    onError: (error: unknown) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'error',
          message: getResponseMessage(error, 'Failed to update patient'),
        }),
      )
    },
  })
}

// POST /api/master/patient/{patientUuid}/clinic/{clinicUuid} — assign clinic, generate MRN
export const useAssociatePatientClinic = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: ({ patientUuid, clinicUuid }: { patientUuid: string; clinicUuid: string }) =>
      patientApi.associateClinic(patientUuid, clinicUuid),
    onMutate: () => dispatch(showLoader()),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: patientKeys.all() })
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'success',
          message: getResponseMessage(response, 'Patient assigned to clinic successfully!'),
        }),
      )
    },
    onError: (error: unknown) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'error',
          message: getResponseMessage(error, 'Failed to assign patient to clinic'),
        }),
      )
    },
  })
}

// PUT /api/master/patient/{patientId}/status/{status}
export const useSetPatientActiveStatus = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: ({ patientId, status }: { patientId: string; status: boolean }) =>
      patientApi.setActiveStatus(patientId, status),
    onMutate: () => dispatch(showLoader()),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: patientKeys.all() })
      dispatch(hideLoader())
      const fallback = variables.status
        ? 'Patient marked as active!'
        : 'Patient marked as inactive!'
      dispatch(
        showSnackbar({ severity: 'success', message: getResponseMessage(response, fallback) }),
      )
    },
    onError: (error: unknown, variables) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'error',
          message: getResponseMessage(
            error,
            variables.status ? 'Failed to mark patient active' : 'Failed to mark patient inactive',
          ),
        }),
      )
    },
  })
}

// PATCH /api/master/patient/{patientId}/archive-status/{status}
export const useArchivePatient = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: ({ patientId, status }: { patientId: string; status: boolean }) =>
      patientApi.archive(patientId, status),
    onMutate: () => dispatch(showLoader()),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: patientKeys.all() })
      dispatch(hideLoader())
      const fallback = variables.status
        ? 'Patient archived successfully!'
        : 'Patient restored successfully!'
      dispatch(
        showSnackbar({ severity: 'success', message: getResponseMessage(response, fallback) }),
      )
    },
    onError: (error: unknown, variables) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'error',
          message: getResponseMessage(
            error,
            variables.status ? 'Failed to archive patient' : 'Failed to restore patient',
          ),
        }),
      )
    },
  })
}

// POST /api/master/patient/resend-invitation/patient/{patientId}
export const useResendPatientInvitation = () => {
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: (patientId: string) => patientApi.resendInvitation(patientId),
    onMutate: () => dispatch(showLoader()),
    onSuccess: (response) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'success',
          message: getResponseMessage(response, 'Invitation resent successfully!'),
        }),
      )
    },
    onError: (error: unknown) => {
      dispatch(hideLoader())
      dispatch(
        showSnackbar({
          severity: 'error',
          message: getResponseMessage(error, 'Failed to resend invitation'),
        }),
      )
    },
  })
}

// PUT /api/master/patient/change-avatar
export const useChangePatientAvatar = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: ({ userId, newAvatar }: { userId: string; newAvatar: string }) =>
      patientApi.changeAvatar(userId, newAvatar),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: patientKeys.all() })
      dispatch(
        showSnackbar({
          severity: 'success',
          message: getResponseMessage(response, 'Avatar updated successfully!'),
        }),
      )
    },
    onError: (error: unknown) => {
      dispatch(
        showSnackbar({
          severity: 'error',
          message: getResponseMessage(error, 'Failed to update avatar'),
        }),
      )
    },
  })
}
